import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/contexts/AdminAuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/components/ui/sonner';
import {
  User,
  Mail,
  Shield,
  Calendar,
  Clock,
  Activity,
  Phone,
  Building,
  MapPin,
  Globe,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle,
  UserCheck,
  UserX
} from 'lucide-react';

interface UserDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail?: string;
}

interface DetailedUser {
  _id: string;
  firebaseUid: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  isBlocked: boolean;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  profile?: {
    fullName?: string;
    workEmail?: string;
    company?: string;
    jobTitle?: string;
    phone?: string;
    location?: string;
    website?: string;
    userType?: string;
    userTypeOther?: string;
    deploymentInfo?: string[];
  };
  preferences?: {
    engagementTiming?: string;
    wantsDemo?: boolean;
    stayConnected?: {
      productUpdates?: boolean;
      ndaAccess?: boolean;
    };
    preferredContact?: string;
    hearAboutUs?: string[];
    hearAboutUsOther?: string;
    additionalInfo?: string;
  };
  usage: {
    leadsScraped?: number;
    emailsSent?: number;
    filesUploaded?: number;
    aiChats?: number;
    lastActivity?: string;
  };
  subscription: {
    plan?: string;
    status?: string;
  };
}

interface ActivityLog {
  _id: string;
  action: string;
  details: any;
  createdAt: string;
  ipAddress?: string;
  userAgent?: string;
}

interface SupportRequest {
  _id: string;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
}

interface UserDetailsData {
  user: DetailedUser;
  activityLogs: ActivityLog[];
  supportRequests: SupportRequest[];
}

export function UserDetailsModal({ open, onOpenChange, userId, userEmail }: UserDetailsModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null);

  useEffect(() => {
    if (open && userId) {
      loadUserDetails();
    }
  }, [open, userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminAPI.getUserDetails(userId);
      setUserDetails(response.data);
    } catch (error: any) {
      console.error('Error loading user details:', error);
      setError(error.message || 'Failed to load user details');
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'root_admin':
        return (
          <Badge className="bg-purple-100 text-purple-800 border-purple-200">
            <Shield className="h-3 w-3 mr-1" />
            Root Admin
          </Badge>
        );
      case 'admin':
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <User className="h-3 w-3 mr-1" />
            User
          </Badge>
        );
    }
  };

  const getStatusBadge = (user: DetailedUser) => {
    if (user.isBlocked) {
      return (
        <Badge variant="destructive">
          <UserX className="h-3 w-3 mr-1" />
          Blocked
        </Badge>
      );
    }
    if (user.isActive) {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <UserCheck className="h-3 w-3 mr-1" />
          Active
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        <Clock className="h-3 w-3 mr-1" />
        Inactive
      </Badge>
    );
  };

  const renderProfileSection = (user: DetailedUser) => {
    const profile = user.profile || {};
    
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <User className="h-5 w-5" />
          Profile Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Full Name</label>
              <p className="text-sm">{profile.fullName || user.displayName || 'Not provided'}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Work Email</label>
              <p className="text-sm">{profile.workEmail || 'Not provided'}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Company</label>
              <p className="text-sm flex items-center gap-1">
                {profile.company ? (
                  <>
                    <Building className="h-3 w-3" />
                    {profile.company}
                  </>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Job Title</label>
              <p className="text-sm">{profile.jobTitle || 'Not provided'}</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Phone</label>
              <p className="text-sm flex items-center gap-1">
                {profile.phone ? (
                  <>
                    <Phone className="h-3 w-3" />
                    {profile.phone}
                  </>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Location</label>
              <p className="text-sm flex items-center gap-1">
                {profile.location ? (
                  <>
                    <MapPin className="h-3 w-3" />
                    {profile.location}
                  </>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Website</label>
              <p className="text-sm flex items-center gap-1">
                {profile.website ? (
                  <>
                    <Globe className="h-3 w-3" />
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {profile.website}
                    </a>
                  </>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">User Type</label>
              <p className="text-sm">{profile.userType || profile.userTypeOther || 'Not provided'}</p>
            </div>
          </div>
        </div>
        
        {profile.deploymentInfo && profile.deploymentInfo.length > 0 && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">Deployment Info</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {profile.deploymentInfo.map((info, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {info}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            User Details
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="space-y-6 pr-4">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="ml-2">Loading user details...</span>
              </div>
            )}
            
            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-red-800">{error}</span>
              </div>
            )}
            
            {userDetails && (
              <>
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Basic Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Email</label>
                        <p className="text-sm flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {userDetails.user.email}
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Firebase UID</label>
                        <p className="text-sm font-mono text-xs bg-gray-100 p-1 rounded">
                          {userDetails.user.firebaseUid}
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Role</label>
                        <div className="mt-1">
                          {getRoleBadge(userDetails.user.role)}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Status</label>
                        <div className="mt-1">
                          {getStatusBadge(userDetails.user)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Created At</label>
                        <p className="text-sm flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(userDetails.user.createdAt)}
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Last Login</label>
                        <p className="text-sm flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(userDetails.user.lastLoginAt)}
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Email Verified</label>
                        <p className="text-sm">
                          {userDetails.user.emailVerified ? (
                            <Badge className="bg-green-100 text-green-800">Verified</Badge>
                          ) : (
                            <Badge variant="secondary">Not Verified</Badge>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                {/* Profile Information */}
                {renderProfileSection(userDetails.user)}
                
                <Separator />
                
                {/* Usage Statistics */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Usage Statistics
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {userDetails.user.usage.leadsScraped || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Leads Scraped</div>
                    </div>
                    
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {userDetails.user.usage.emailsSent || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Emails Sent</div>
                    </div>
                    
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {userDetails.user.usage.filesUploaded || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Files Uploaded</div>
                    </div>
                    
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {userDetails.user.usage.aiChats || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">AI Chats</div>
                    </div>
                  </div>
                  
                  {userDetails.user.usage.lastActivity && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Activity</label>
                      <p className="text-sm">{formatDate(userDetails.user.usage.lastActivity)}</p>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Preferences */}
                {userDetails.user.preferences && (
                  <>
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        User Preferences
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Engagement Timing</label>
                            <p className="text-sm">{userDetails.user.preferences.engagementTiming || 'Not specified'}</p>
                          </div>

                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Wants Demo</label>
                            <p className="text-sm">
                              {userDetails.user.preferences.wantsDemo !== undefined
                                ? (userDetails.user.preferences.wantsDemo ? 'Yes' : 'No')
                                : 'Not specified'
                              }
                            </p>
                          </div>

                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Preferred Contact</label>
                            <p className="text-sm">{userDetails.user.preferences.preferredContact || 'Not specified'}</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">Stay Connected</label>
                            <div className="text-sm space-y-1">
                              <div>Product Updates: {userDetails.user.preferences.stayConnected?.productUpdates ? 'Yes' : 'No'}</div>
                              <div>NDA Access: {userDetails.user.preferences.stayConnected?.ndaAccess ? 'Yes' : 'No'}</div>
                            </div>
                          </div>

                          {userDetails.user.preferences.hearAboutUs && userDetails.user.preferences.hearAboutUs.length > 0 && (
                            <div>
                              <label className="text-sm font-medium text-muted-foreground">How They Heard About Us</label>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {userDetails.user.preferences.hearAboutUs.map((source, index) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {source}
                                  </Badge>
                                ))}
                              </div>
                              {userDetails.user.preferences.hearAboutUsOther && (
                                <p className="text-sm mt-1 text-muted-foreground">
                                  Other: {userDetails.user.preferences.hearAboutUsOther}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {userDetails.user.preferences.additionalInfo && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Additional Information</label>
                          <p className="text-sm mt-1 p-3 bg-gray-50 rounded-lg">
                            {userDetails.user.preferences.additionalInfo}
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator />
                  </>
                )}

                {/* Recent Activity */}
                {userDetails.activityLogs && userDetails.activityLogs.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Recent Activity
                    </h3>

                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userDetails.activityLogs.slice(0, 10).map((log) => (
                        <div key={log._id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">{log.action.replace(/_/g, ' ').toUpperCase()}</p>
                              <span className="text-xs text-muted-foreground">{formatDate(log.createdAt)}</span>
                            </div>
                            {log.details && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                              </p>
                            )}
                            {log.ipAddress && (
                              <p className="text-xs text-muted-foreground">IP: {log.ipAddress}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Support Requests */}
                {userDetails.supportRequests && userDetails.supportRequests.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Support Requests
                      </h3>

                      <div className="space-y-2">
                        {userDetails.supportRequests.map((request) => (
                          <div key={request._id} className="p-3 border border-border rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-medium">{request.subject}</h4>
                              <div className="flex items-center gap-2">
                                <Badge variant={request.status === 'open' ? 'default' : 'secondary'}>
                                  {request.status}
                                </Badge>
                                <Badge variant={request.priority === 'high' ? 'destructive' : 'outline'}>
                                  {request.priority}
                                </Badge>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Created: {formatDate(request.createdAt)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
